import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  FlatList,
  TextInput,
  Dimensions,
  ViewStyle,
  TextStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { DLODropdownOption } from '../types/dlo.types';

interface DLODropdownProps {
  label?: string;
  placeholder?: string;
  value?: DLODropdownOption | DLODropdownOption[] | null;
  options: DLODropdownOption[];
  onSelect: (option: DLODropdownOption | DLODropdownOption[] | null) => void;
  showLabel?: boolean;
  style?: ViewStyle;
  // Enhanced features from CrossPlatformDropdown
  selection?: 'single' | 'multiple';
  isClearable?: boolean;
  isDisabled?: boolean;
  readOnly?: boolean;
  isRequired?: boolean;
  size?: 'small' | 'medium' | 'large';
  helperText?: string;
  feedbackType?: 'error' | 'success' | 'warning' | 'info';
  dropdownHeader?: string;
  disableSorting?: boolean;
  hideSearch?: boolean;
  testID?: string;
  // For table cell usage
  fullWidth?: boolean;
  minDropdownWidth?: number;
  // Position control
  placement?: 'auto' | 'top' | 'bottom';
  // Vertical offset between field and dropdown
  offset?: number;
  // Remove top margin for tighter connection
  removeMarginTop?: boolean;
  placeholderStyle?: TextStyle;
  // Custom text input support
  customTextInput?: {
    show: boolean;
    placeholder: string;
    value: string;
    onTextChange: (text: string) => void;
    onSubmit?: () => void;
    validate?: (text: string) => boolean;
    errorMessage?: string;
  };
}

const DropdownArrowIcon = ({ isOpen, isReadOnly, isDisabled, size = 'medium' }: { 
  isOpen: boolean; 
  isReadOnly?: boolean; 
  isDisabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}) => {
  const iconSize = size === 'small' ? 14 : size === 'large' ? 18 : 16;
  
  if (isReadOnly) {
    // Show lock icon for read-only state
    return (
      <Svg width={iconSize} height={iconSize} viewBox="0 0 16 16" fill="none">
        <Path 
          d="M4 7V5C4 2.79086 5.79086 1 8 1C10.2091 1 12 2.79086 12 5V7H13C13.5523 7 14 7.44772 14 8V14C14 14.5523 13.5523 15 13 15H3C2.44772 15 2 14.5523 2 14V8C2 7.44772 2.44772 7 3 7H4ZM6 7H10V5C10 3.89543 9.10457 3 8 3C6.89543 3 6 3.89543 6 5V7Z"
          fill={isDisabled ? "#ADB5BD" : "#6C757D"}
        />
      </Svg>
    );
  }
  
  if (isDisabled) {
    // Show disabled arrow
    return (
      <Svg width={iconSize} height={iconSize} viewBox="0 0 16 16" fill="none">
        <Path 
          d={isOpen 
            ? "M11.06 10.2733L8 7.22L4.94 10.2733L4 9.33333L8 5.33333L12 9.33333L11.06 10.2733Z"
            : "M4.94 5.72667L8 8.78L11.06 5.72667L12 6.66667L8 10.6667L4 6.66667L4.94 5.72667Z"
          }
          fill="#ADB5BD"
        />
      </Svg>
    );
  }
  
  // Default arrow
  return (
    <Svg width={iconSize} height={iconSize} viewBox="0 0 16 16" fill="none">
      <Path 
        d={isOpen 
          ? "M11.06 10.2733L8 7.22L4.94 10.2733L4 9.33333L8 5.33333L12 9.33333L11.06 10.2733Z"
          : "M4.94 5.72667L8 8.78L11.06 5.72667L12 6.66667L8 10.6667L4 6.66667L4.94 5.72667Z"
        }
        fill="#3A3A3A"
      />
    </Svg>
  );
};

const DLODropdown: React.FC<DLODropdownProps> = ({
  label = 'Location',
  placeholder = 'Select option',
  value,
  options,
  onSelect,
  showLabel = true,
  style,
  selection = 'single',
  isClearable = false,
  isDisabled = false,
  readOnly = false,
  isRequired = false,
  size = 'medium',
  helperText,
  feedbackType = 'info',
  dropdownHeader,
  disableSorting = false,
  hideSearch = false,
  testID,
  fullWidth = false,
  minDropdownWidth = 200,
  placement = 'auto',
  offset = 2,
  removeMarginTop = false,
  placeholderStyle,
  customTextInput,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<DLODropdownOption[]>([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [hoveredItemId, setHoveredItemId] = useState<string | number | null>(null);
  const [dropdownLayout, setDropdownLayout] = useState({
    pageX: 0,
    pageY: 0,
    width: 0,
    height: 0,
  });
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom');

  const dropdownRef = useRef<any>(null);

  // Initialize selected items from value prop with validation
  useEffect(() => {
    if (value) {
      const items = Array.isArray(value) ? value : [value];
      // Validate that all items exist in options
      const validItems = items.filter(item => 
        options.some(option => option.id === item.id)
      );
      setSelectedItems(validItems);
    } else {
      setSelectedItems([]);
    }
  }, [value, options]);

  // Memoized styles for better performance
  const dropdownStylesConfig = useMemo(() => ({
    id: 'dlo-dropdown-styles',
    css: `
      .dropdown-menu {
        z-index: 10000 !important;
        position: fixed !important;
        background: white;
        border-radius: 4px;
        border: 1px solid #DFDFDF;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }
      .dropdown-search input {
        z-index: 10001 !important;
        outline: none !important;
      }
      .dropdown-portal {
        z-index: 9999 !important;
        pointer-events: none;
      }
      .dropdown-portal > * {
        pointer-events: auto;
      }
      .dropdown-menu * {
        box-sizing: border-box;
      }
      .dropdown-menu-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .dropdown-menu-scrollbar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      .dropdown-menu-scrollbar::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
      }
      .dropdown-menu-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }
    `,
  }), []);

  // Inject styles only once for web platform
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      if (!document.getElementById(dropdownStylesConfig.id)) {
        const style = document.createElement('style');
        style.id = dropdownStylesConfig.id;
        style.textContent = dropdownStylesConfig.css;
        document.head.appendChild(style);
      }
    }
  }, [dropdownStylesConfig]);

  // Update dropdown layout for positioning
  const updateDropdownLayout = useCallback(() => {
    if (!dropdownRef.current) return;

    if (Platform.OS === 'web') {
      const rect = (dropdownRef.current as any).getBoundingClientRect?.();
      if (!rect) return;

      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      // Calculate viewport-relative positions
      const viewportTop = rect.top;
      const viewportBottom = rect.bottom;
      const viewportLeft = rect.left;
      
      // Calculate absolute positions for fixed positioning
      const absoluteTop = viewportTop + scrollTop;
      const absoluteBottom = viewportBottom + scrollTop;
      const absoluteLeft = viewportLeft + scrollLeft;
      
      const spaceBelow = windowHeight - viewportBottom;
      const spaceAbove = viewportTop;
      const menuHeight = Math.min(300, windowHeight * 0.6);

      // Choose position based on available space and placement prop
      let newPosition: 'top' | 'bottom';
      if (placement === 'top') {
        newPosition = 'top';
      } else if (placement === 'bottom') {
        newPosition = 'bottom';
      } else {
        // Auto placement based on available space
        newPosition = spaceBelow >= menuHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top';
      }
      setPosition(newPosition);

      // Calculate Y position for fixed positioning
      let yPosition: number;
      if (newPosition === 'bottom') {
        yPosition = viewportBottom + offset;
      } else {
        yPosition = viewportTop - menuHeight - offset;
      }
      
      // Ensure dropdown doesn't go off-screen vertically
      if (yPosition < 0) {
        yPosition = 10;
      } else if (yPosition + menuHeight > windowHeight) {
        yPosition = windowHeight - menuHeight - 10;
      }

      // Calculate X position for fixed positioning
      const menuWidth = fullWidth ? 
        Math.max(rect.width, minDropdownWidth) : 
        Math.max(rect.width, minDropdownWidth);
        
      let xPosition = viewportLeft;
      
      // Ensure dropdown doesn't go off-screen horizontally
      if (xPosition + menuWidth > windowWidth) {
        xPosition = windowWidth - menuWidth - 10;
      }
      if (xPosition < 0) {
        xPosition = 10;
      }

      setDropdownLayout({
        pageX: xPosition,
        pageY: yPosition,
        width: menuWidth,
        height: menuHeight,
      });
    } else {
      // React Native positioning logic remains the same
      dropdownRef.current.measureInWindow((x: number, y: number, width: number, height: number) => {
        const windowHeight = Dimensions.get('window').height;
        const windowWidth = Dimensions.get('window').width;
        const spaceBelow = windowHeight - (y + height);
        const spaceAbove = y;
        const menuHeight = Math.min(300, windowHeight * 0.6);

        let newPosition: 'top' | 'bottom';
        if (placement === 'top') {
          newPosition = 'top';
        } else if (placement === 'bottom') {
          newPosition = 'bottom';
        } else {
          newPosition = spaceBelow >= menuHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top';
        }
        setPosition(newPosition);

        let yPosition = newPosition === 'bottom' ? y + height + offset : y - menuHeight - offset;
        
        if (yPosition < 0) {
          yPosition = 10;
        } else if (yPosition + menuHeight > windowHeight) {
          yPosition = windowHeight - menuHeight - 10;
        }

        let xPosition = x;
        const menuWidth = fullWidth ? 
          Math.max(width, minDropdownWidth) : 
          Math.max(width, minDropdownWidth);
        if (xPosition + menuWidth > windowWidth) {
          xPosition = windowWidth - menuWidth - 10;
        }
        if (xPosition < 0) {
          xPosition = 10;
        }

        setDropdownLayout({
          pageX: xPosition,
          pageY: yPosition,
          width: menuWidth,
          height: menuHeight,
        });
      });
    }
  }, [fullWidth, minDropdownWidth, offset, placement]);

  // Utility: Check if element is in viewport (web only)
  const isElementInViewport = useCallback((el: HTMLElement | null) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }, []);

  // Handle outside clicks and scroll events on web
  useEffect(() => {
    if (Platform.OS === 'web' && isOpen) {
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        
        // Allow clicks on dropdown options and menu items
        if (
          target.closest('.dropdown-menu') ||
          target.closest('.dropdown-search') ||
          target.closest('input') ||
          target.tagName === 'INPUT' ||
          target.closest('[role="button"]') || // Allow option clicks
          target.closest('.option') // Allow option clicks
        ) {
          // Don't stop propagation for option clicks - let them through
          if (!target.closest('.dropdown-search') && !target.closest('input')) {
            return; // Allow option clicks to bubble up
          }
          e.stopPropagation();
          return;
        }

        if (!target.closest('.modal-content')) {
          setIsOpen(false);
        }
      };

      const handleScroll = () => {
        if (isOpen) {
          updateDropdownLayout();
          // Close dropdown if trigger is not visible
          const trigger = dropdownRef.current as HTMLElement | null;
          if (trigger && !isElementInViewport(trigger)) {
            setIsOpen(false);
          }
        }
      };

      const handleResize = () => {
        if (isOpen) {
          updateDropdownLayout();
          // Close dropdown if trigger is not visible
          const trigger = dropdownRef.current as HTMLElement | null;
          if (trigger && !isElementInViewport(trigger)) {
            setIsOpen(false);
          }
        }
      };

      document.addEventListener('click', handleClick, true);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        document.removeEventListener('click', handleClick, true);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, updateDropdownLayout, isElementInViewport]);

  // Clear search text when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
      setDebouncedSearchText('');
    }
  }, [isOpen]);

  // Optimized search handler with useCallback
  const handleSearchChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  // Optimized toggle handler with useCallback
  const handleToggleDropdown = useCallback(() => {
    if (readOnly || isDisabled) {
      return;
    }
    if (!isOpen) {
      updateDropdownLayout();
    }
    setIsOpen(!isOpen);
  }, [readOnly, isDisabled, isOpen, updateDropdownLayout]);

  // Debounce search text for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText]);

  const toggleDropdown = handleToggleDropdown;

  const handleSelect = useCallback((item: DLODropdownOption) => {
    console.log("handleSelect called with item:", item, "selection mode:", selection);
    
    if (selection === 'single') {
      console.log("Single selection - setting selected items and calling onSelect");
      setSelectedItems([item]);
      onSelect(item);
      setIsOpen(false);
    } else {
      const isSelected = selectedItems.some(
        (selected) => selected.id === item.id
      );
      const newSelection = isSelected
        ? selectedItems.filter((selected) => selected.id !== item.id)
        : [...selectedItems, item];
      console.log("Multi selection - new selection:", newSelection);
      setSelectedItems(newSelection);
      onSelect(newSelection);
      // Keep dropdown open in multi-select
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  }, [selection, selectedItems, onSelect, isOpen]);

  const clearSelection = useCallback((e?: any) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedItems([]);
    onSelect(null);
  }, [onSelect]);

  // Sort options if not disabled - memoized for performance
  const sortedOptions = useMemo(() => {
    if (!options || options.length === 0) return [];
    
    const allItem = options.find((item) => item.label === 'All');
    const otherItems = options.filter((item) => item.label !== 'All');

    const sortedOtherItems = disableSorting
      ? otherItems
      : otherItems.sort((a, b) => a.label.localeCompare(b.label));

    return allItem ? [allItem, ...sortedOtherItems] : sortedOtherItems;
  }, [options, disableSorting]);

  // Filter options based on search text - memoized for performance
  const filteredItems = useMemo(() => {
    if (!debouncedSearchText.trim()) return sortedOptions;
    
    const searchLower = debouncedSearchText.toLowerCase();
    return sortedOptions.filter((item) =>
      item.label.toLowerCase().includes(searchLower)
    );
  }, [sortedOptions, debouncedSearchText]);

  const getDisplayText = useMemo(() => {
    if (selectedItems.length === 0) {
      return placeholder;
    }
    return selection === 'single'
      ? selectedItems[0].label
      : selectedItems.map((item) => item.label).join(', ');
  }, [selectedItems, placeholder, selection]);

  // Memoize selected item IDs for faster lookup
  const selectedItemIds = useMemo(() => 
    new Set(selectedItems.map(item => item.id)), 
    [selectedItems]
  );

  // Optimized mouse event handlers
  const handleMouseEnter = useCallback((itemId: string | number) => {
    setHoveredItemId(itemId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredItemId(null);
  }, []);

  // Optimized item press handler
  const handleItemPress = useCallback((item: DLODropdownOption) => {
    console.log("Option clicked:", item); // Debug log
    handleSelect(item);
  }, [handleSelect]);

  const renderItem = useCallback(({ item }: { item: DLODropdownOption }) => {
    const isSelected = selectedItemIds.has(item.id);
    const isHovered = Platform.OS === 'web' && hoveredItemId === item.id;
    const isItemDisabled = item.disabled || false;
    
    return (
      <TouchableOpacity
        style={[
          styles.option,
          isSelected && styles.selectedOption,
          isHovered && !isItemDisabled && styles.hoverItem,
          isItemDisabled && styles.disabledOption,
        ]}
        onPress={isItemDisabled ? undefined : () => handleItemPress(item)}
        disabled={isItemDisabled}
        accessibilityRole="button"
        accessibilityLabel={`${item.label}${isSelected ? ', selected' : ''}${isItemDisabled ? ', disabled' : ''}`}
        {...(Platform.OS === 'web'
          ? {
              onMouseEnter: isItemDisabled ? undefined : () => handleMouseEnter(item.id),
              onMouseLeave: isItemDisabled ? undefined : handleMouseLeave,
              className: 'option', // Add className for web event targeting
            }
          : {})}
      >
        <Text style={[styles.optionText, isItemDisabled && styles.disabledOptionText]} numberOfLines={1}>
          {item.label}
        </Text>
        {isSelected && (
          <MaterialIcons name="check" size={16} color="#0066CC" />
        )}
      </TouchableOpacity>
    );
  }, [selectedItemIds, hoveredItemId, handleItemPress, handleMouseEnter, handleMouseLeave]);

  // Memoized item layout for FlatList performance
  const getItemLayout = useCallback((data: any, index: number) => ({
    length: 44, // Approximate item height
    offset: 44 * index,
    index,
  }), []);

  // Memoized key extractor for FlatList performance
  const keyExtractor = useCallback((item: DLODropdownOption) => String(item.id), []);

  // Memoized empty list component
  const ListEmptyComponent = useMemo(() => (
    <Text style={styles.emptyText}>No items found</Text>
  ), []);

  return (
    <View style={[styles.container, fullWidth && styles.fullWidthContainer, style]} testID={testID}>
      {showLabel && label && (
        <View style={styles.labelContainer}>
          <Text style={styles.label}>
            {label}
            {isRequired && <Text style={styles.required}> *</Text>}
            {readOnly && !isDisabled && <Text style={styles.readOnlyLabel}> (Read Only)</Text>}
          </Text>
        </View>
      )}
      
      <View style={styles.inputContainer}>
        <TouchableOpacity
          ref={dropdownRef}
          {...(Platform.OS === 'web' ? { className: 'dropdown-trigger' } : {})}
          {...(Platform.OS === 'web' ? {
            'aria-haspopup': 'listbox',
            'aria-expanded': isOpen,
            'aria-label': `${label || 'Dropdown'}, ${getDisplayText}`,
            role: 'combobox'
          } : {})}
          style={[
            styles.field,
            size === 'small' && styles.smallField,
            size === 'large' && styles.largeField,
            readOnly && styles.readOnlyField,
            isDisabled && styles.disabledField,
          ]}
          onPress={(e) => {
            if (e.stopPropagation) {
              e.stopPropagation();
            }
            if (!readOnly && !isDisabled) {
              toggleDropdown();
            }
          }}
          disabled={readOnly || isDisabled}
          activeOpacity={readOnly || isDisabled ? 1 : 0.7}
          accessibilityRole="button"
          accessibilityLabel={`${label || 'Dropdown'}, ${getDisplayText}${readOnly ? ', read only' : ''}${isDisabled ? ', disabled' : ''}`}
          accessibilityHint={readOnly ? "This field is read only" : isDisabled ? "This field is disabled" : "Double tap to open dropdown"}
          {...(Platform.OS === 'web' ? {
            'aria-readonly': readOnly,
            'aria-disabled': isDisabled,
          } : {})}
        >
          <Text
            style={[
              styles.fieldText,
              size === 'small' && styles.smallFieldText,
              size === 'large' && styles.largeFieldText,
              selectedItems.length === 0 && styles.placeholderText,
              selectedItems.length === 0 && placeholderStyle,
              readOnly && styles.readOnlyText,
              isDisabled && styles.disabledText,
            ]}
            numberOfLines={1}
          >
            {getDisplayText}
          </Text>
          <View style={styles.iconContainer}>
            {isClearable && selectedItems.length > 0 && !readOnly && !isDisabled && (
              <TouchableOpacity
                style={styles.clearButton}
                onPress={clearSelection}
                hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
                accessibilityRole="button"
                accessibilityLabel="Clear selection"
              >
                <MaterialIcons name="close" size={14} color="#3A3A3A" />
              </TouchableOpacity>
            )}
            <DropdownArrowIcon 
              isOpen={isOpen} 
              isReadOnly={readOnly} 
              isDisabled={isDisabled} 
              size={size}
            />
          </View>
        </TouchableOpacity>

        {/* Web dropdown using Modal for better positioning */}
        {Platform.OS === 'web' && isOpen && !readOnly && !isDisabled && (
          <Modal
            visible={isOpen}
            transparent
            animationType="none"
            onRequestClose={() => setIsOpen(false)}
          >
            <TouchableOpacity
              style={styles.webModalOverlay}
              activeOpacity={1}
              onPress={(e) => {
                // Only close if clicking directly on the overlay, not on child elements
                if (e.target === e.currentTarget) {
                  setIsOpen(false);
                }
              }}
            >
              <View
                style={[
                  styles.webDropdownMenu,
                  {
                    position: 'absolute',
                    top: dropdownLayout.pageY,
                    left: dropdownLayout.pageX,
                    width: dropdownLayout.width,
                    maxHeight: dropdownLayout.height,
                    marginTop: removeMarginTop ? 0 : 2,
                  },
                ]}
                {...(Platform.OS === 'web' ? { className: 'dropdown-menu' } : {})}
                // Remove the onClick stopPropagation to allow option clicks
              >
                {dropdownHeader && (
                  <Text style={styles.dropdownHeader}>{dropdownHeader}</Text>
                )}

                {!hideSearch && (
                  <View
                    style={styles.searchContainer}
                    {...(Platform.OS === 'web'
                      ? { className: 'dropdown-search' }
                      : {})}
                  >
                    <MaterialIcons
                      name="search"
                      size={16}
                      color="#3A3A3A"
                      style={{ marginRight: 6 }}
                    />
                    <TextInput
                      style={styles.searchInput}
                      placeholder="Search..."
                      value={searchText}
                      onChangeText={handleSearchChange}
                      placeholderTextColor="#6C757D"
                      accessibilityLabel="Search dropdown items"
                      {...(Platform.OS === 'web'
                        ? {
                            onClick: (e: any) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onKeyDown: (e: any) => {
                              e.stopPropagation();
                            },
                          }
                        : {})}
                      onFocus={(e: any) => {
                        if (e.preventDefault) e.preventDefault();
                        if (e.stopPropagation) e.stopPropagation();
                      }}
                    />
                  </View>
                )}

                {/* Custom Text Input */}
                {customTextInput?.show && (
                  <View style={styles.customInputContainer}>
                    <Text style={styles.customInputLabel}>{customTextInput.placeholder}</Text>
                    <TextInput
                      style={[
                        styles.customInput,
                        customTextInput.validate && !customTextInput.validate(customTextInput.value) && styles.customInputError
                      ]}
                      placeholder="HH:MM"
                      value={customTextInput.value}
                      onChangeText={customTextInput.onTextChange}
                      onSubmitEditing={customTextInput.onSubmit}
                      placeholderTextColor="#6C757D"
                      maxLength={5}
                      keyboardType="numeric"
                    />
                    {customTextInput.validate && !customTextInput.validate(customTextInput.value) && customTextInput.errorMessage && (
                      <Text style={styles.customInputErrorText}>{customTextInput.errorMessage}</Text>
                    )}
                  </View>
                )}

                <FlatList
                  data={filteredItems}
                  renderItem={renderItem}
                  keyExtractor={keyExtractor}
                  keyboardShouldPersistTaps="always"
                  style={styles.optionsList}
                  showsVerticalScrollIndicator={true}
                  nestedScrollEnabled={true}
                  removeClippedSubviews={true}
                  maxToRenderPerBatch={10}
                  windowSize={10}
                  initialNumToRender={10}
                  getItemLayout={getItemLayout}
                  // Remove the onClick stopPropagation to allow option clicks
                  onScroll={(e) => {
                    // Only stop scroll propagation, not clicks
                    if (e?.stopPropagation) {
                      e.stopPropagation();
                    }
                  }}
                  // Remove touch event blocking for options to work
                  ListEmptyComponent={
                    <Text style={styles.emptyText}>No items found</Text>
                  }
                />
              </View>
            </TouchableOpacity>
          </Modal>
        )}
      </View>

      {helperText && (
        <Text
          style={[
            styles.helperText,
            feedbackType === 'error' && styles.errorText,
            feedbackType === 'success' && styles.successText,
            feedbackType === 'warning' && styles.warningText,
          ]}
        >
          {helperText}
        </Text>
      )}

      {/* Read-only state indicator */}
      {readOnly && !helperText && (
        <Text style={styles.readOnlyHelperText}>
          This field is read-only and cannot be modified
        </Text>
      )}

      <Modal
        visible={isOpen && Platform.OS !== 'web' && !readOnly && !isDisabled}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
        supportedOrientations={['portrait', 'landscape']}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <View
            {...(Platform.OS === 'web' ? { className: 'modal-content' } : {})}
            style={[
              styles.modalContent,
              {
                position: 'absolute',
                top: dropdownLayout.pageY,
                left: dropdownLayout.pageX,
                width: dropdownLayout.width,
                maxHeight: dropdownLayout.height,
                marginTop: removeMarginTop ? 0 : 2,
              },
            ]}
            {...(Platform.OS === 'web'
              ? { onClick: (e: any) => e.stopPropagation() }
              : {})}
          >
            <View
              style={styles.dropdownMenu}
              {...(Platform.OS === 'web' ? { className: 'dropdown-menu' } : {})}
              {...(Platform.OS === 'web'
                ? { onClick: (e: any) => e.stopPropagation() }
                : {})}
            >
              {dropdownHeader && (
                <Text style={styles.dropdownHeader}>{dropdownHeader}</Text>
              )}

              <View style={styles.modalHeader}>
                <Text style={styles.modalHeaderText}>{label}</Text>
                <TouchableOpacity
                  onPress={() => setIsOpen(false)}
                  style={styles.closeButton}
                >
                  <MaterialIcons name="close" size={20} color="#3A3A3A" />
                </TouchableOpacity>
              </View>

              {!hideSearch && (
                <View
                  style={styles.searchContainer}
                  {...(Platform.OS === 'web'
                    ? { className: 'dropdown-search' }
                    : {})}
                >
                  <MaterialIcons
                    name="search"
                    size={16}
                    color="#3A3A3A"
                    style={{ marginRight: 6 }}
                  />
                  <TextInput
                    style={styles.searchInput}
                    placeholder="Search..."
                    value={searchText}
                    onChangeText={handleSearchChange}
                    placeholderTextColor="#6C757D"
                    accessibilityLabel="Search dropdown items"
                  />
                </View>
              )}

              {/* Custom Text Input */}
              {customTextInput?.show && (
                <View style={styles.customInputContainer}>
                  <Text style={styles.customInputLabel}>{customTextInput.placeholder}</Text>
                  <TextInput
                    style={[
                      styles.customInput,
                      customTextInput.validate && !customTextInput.validate(customTextInput.value) && styles.customInputError
                    ]}
                    placeholder="HH:MM"
                    value={customTextInput.value}
                    onChangeText={customTextInput.onTextChange}
                    onSubmitEditing={customTextInput.onSubmit}
                    placeholderTextColor="#6C757D"
                    maxLength={5}
                    keyboardType="numeric"
                  />
                  {customTextInput.validate && !customTextInput.validate(customTextInput.value) && customTextInput.errorMessage && (
                    <Text style={styles.customInputErrorText}>{customTextInput.errorMessage}</Text>
                  )}
                </View>
              )}

              <FlatList
                data={filteredItems}
                renderItem={renderItem}
                keyExtractor={keyExtractor}
                keyboardShouldPersistTaps="always"
                style={styles.optionsList}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                removeClippedSubviews={true}
                maxToRenderPerBatch={10}
                windowSize={10}
                initialNumToRender={10}
                getItemLayout={getItemLayout}
                ListEmptyComponent={ListEmptyComponent}
              />
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: 8,
  },
  fullWidthContainer: {
    flex: 1,
    minWidth: 0, // Allow shrinking in flex containers
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
  },
  required: {
    color: '#DC3545',
  },
  readOnlyLabel: {
    color: '#6C757D',
    fontStyle: 'italic',
    fontSize: 11,
  },
  inputContainer: {
    position: 'relative',
  },
  field: {
    flexDirection: 'row',
    width: '100%',
    minHeight: 32,
    paddingHorizontal: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
  },
  smallField: {
    minHeight: 28,
    paddingHorizontal: 6,
  },
  largeField: {
    minHeight: 40,
    paddingHorizontal: 12,
  },
  readOnly: {
    opacity: 0.7,
    backgroundColor: '#F8F9FA',
  },
  readOnlyField: {
    backgroundColor: '#F8F9FA',
    borderColor: '#E9ECEF',
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
      },
    }),
  },
  disabledField: {
    opacity: 0.5,
    backgroundColor: '#F8F9FA',
    borderColor: '#E9ECEF',
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
      },
    }),
  },
  fieldText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    flex: 1,
  },
  smallFieldText: {
    fontSize: 12,
    lineHeight: 16,
  },
  largeFieldText: {
    fontSize: 16,
    lineHeight: 24,
  },
  placeholderText: {
    color: '#6C757D',
    // fontSize: 14,
    // textAlign: 'center', // Center the placeholder text
    // width: '100%',
  },
  readOnlyText: {
    color: '#6C757D',
  },
  disabledText: {
    color: '#ADB5BD',
  },
  iconContainer: {
    flexDirection: 'row',
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 4,
  },
  clearButton: {
    marginRight: 4,
    padding: 2,
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
    color: '#0066CC',
  },
  errorText: {
    color: '#DC3545',
  },
  successText: {
    color: '#28A745',
  },
  warningText: {
    color: '#FFC107',
  },
  readOnlyHelperText: {
    fontSize: 12,
    marginTop: 4,
    color: '#6C757D',
    fontStyle: 'italic',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    marginTop: 2,
    maxHeight: 300,
    width: '100%',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 20,
    zIndex: 9999,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transform: 'translateZ(0)', // Force hardware acceleration
      } as any,
    }),
  } as ViewStyle,
  dropdownMenu: {
    backgroundColor: '#FBFCFE',
    borderRadius: 4,
    maxHeight: 300,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 10,
      },
      web: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        display: 'flex',
        flexDirection: 'column',
      },
    }),
  },
  dropdownHeader: {
    padding: 12,
    fontSize: 14,
    fontWeight: '500',
    color: '#3A3A3A',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    width: '100%',
    maxWidth: '100%',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#3A3A3A',
    height: 32,
    padding: 6,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
    minWidth: 100, // Minimum width for usability
    maxWidth: '100%',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    ...Platform.select({
      web: {
        outline: 'none',
        boxSizing: 'border-box',
      },
    }),
  },
  optionsList: {
    maxHeight: 200,
    backgroundColor: '#FBFCFE',
    ...Platform.select({
      web: {
        overflowY: 'auto',
        scrollbarWidth: 'thin',
      },
      default: {
        overflow: 'scroll',
      },
    }),
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  selectedOption: {
    backgroundColor: '#E8F4FD',
  },
  hoverItem: {
    backgroundColor: '#F5F5F5',
  },
  optionText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    flex: 1,
  },
  selectedOptionText: {
    color: '#0066CC',
    fontWeight: '500',
  },
  disabledOption: {
    opacity: 0.5,
    backgroundColor: '#F8F9FA',
  },
  disabledOptionText: {
    color: '#ADB5BD',
  },
  emptyText: {
    padding: 16,
    textAlign: 'center',
    color: '#6C757D',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
  },
  modalOverlay: Platform.select({
    web: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    default: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
  }) as ViewStyle,
  modalContent: {
    backgroundColor: '#FBFCFE',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '80%',
    ...Platform.select({
      web: {
        overflow: 'visible' as const,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  modalHeaderText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  closeButton: {
    padding: 8,
    borderRadius: 4,
  },
  closeButtonText: {
    color: '#3A3A3A',
    fontSize: 18,
    fontWeight: '400',
  },
  webModalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent', // Transparent for web
    ...Platform.select({
      web: {
        position: 'fixed' as any,
        zIndex: 9999,
      },
    }),
  } as ViewStyle,
  webDropdownMenu: {
    backgroundColor: '#FBFCFE',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    maxHeight: 300,
    width: '100%',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 10,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
      } as any,
    }),
  } as ViewStyle,
  // Custom text input styles
  customInputContainer: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    backgroundColor: '#F8F9FA',
  },
  customInputLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#3A3A3A',
    marginBottom: 8,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  customInput: {
    height: 36,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    paddingHorizontal: 12,
    fontSize: 14,
    color: '#3A3A3A',
    backgroundColor: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    ...Platform.select({
      web: {
        outline: 'none',
      },
    }),
  },
  customInputError: {
    borderColor: '#C0C0C0',
    backgroundColor: '#FFFFFF',
  },
  customInputErrorText: {
    fontSize: 11,
    color: '#6C757D',
    marginTop: 4,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLODropdown;
